// ADR 1.10.26p §1 — a visitor with no stored token is sent to `/signin`
// before the router exists.
//
// A data router resolves every matched route's `lazy` before it renders any
// matched element, and the guards are elements (5.9.26p §5). So a token-less
// visit to `/` used to import Home — and through it the wasm facade, whose
// top-level `await` downloads and instantiates ~730 KB — before
// `RequireSignedIn` could render its `<Navigate>`. `main.tsx` asks this module
// first and rewrites the entry, so `createBrowserRouter` never matches Home.
//
// Pure: no window, no storage. It answers only the one case a synchronous read
// can answer with certainty — no token means signed out. A stored token the
// server has revoked takes the guard's path, which stays the authority.
//
// Imports `react-router-dom`'s `matchRoutes` only, never `../wasm` or the
// route table itself, so it is a boot-graph leaf (AC8).

import { matchRoutes, type RouteObject } from 'react-router-dom'

/** The guard route's id; `router.tsx` sets it on the `RequireSignedIn` pathless route. */
export const SIGNED_IN_ROUTE_ID = 'signed-in'

/** The one spelling of the guard's redirect, shared with `RequireSignedIn`. */
export function signInPath(pathname: string, search: string): string {
  return `/signin?next=${encodeURIComponent(`${pathname}${search}`)}`
}

/**
 * The URL to replace the entry with, or null to leave it. Public routes and the
 * not-found catch-all match outside the guard, so they are untouched by
 * construction — there is no path list to keep in step.
 */
export function signedOutEntry(
  routes: RouteObject[],
  location: { pathname: string; search: string },
  tokenPresent: boolean,
): string | null {
  if (tokenPresent) return null
  const matches = matchRoutes(routes, { pathname: location.pathname }) ?? []
  const guarded = matches.some((match) => match.route.id === SIGNED_IN_ROUTE_ID)
  return guarded ? signInPath(location.pathname, location.search) : null
}
